import React, { useState, useEffect } from 'react';
import {
  Settings,
  LogOut,
  CheckCircle,
  X,
  Clock,
  TrendingUp,
  MessageSquare,
  User,
  Bell,
  Shield,
  Users,
  Trash2,
  Edit3,
  Send,
  Calendar,
  Target,
  Activity,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useGoals } from '../hooks/useGoals';
import { useMessages } from '../hooks/useMessages';
import { useToast } from '../hooks/useToast';
import { useRetry } from '../hooks/useRetry';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import LoadingSpinner from './LoadingSpinner';
import SkeletonLoader from './SkeletonLoader';
import UnifiedOnboardingFlow from './onboarding/UnifiedOnboardingFlow';
import InteractionFeed from './InteractionFeed';
import FeedbackForm from './FeedbackForm';
import SubscriptionModal from './SubscriptionModal';
import { analytics, trackGoalCreated, trackGoalCompleted } from '../utils/analytics';

export default function Dashboard() {
  const { userProfile, userPreferences, signOut, updateUserProfile, updateUserPreferences } = useAuth();
  const { goals, createGoal, completeGoal, getTodaysGoal, getWeeklyStats, getMonthlyCompletionRate, getCurrentStreak, loading: goalsLoading } = useGoals();
  const { sendTestMessage } = useMessages();
  const { success, error: showError } = useToast();
  const { retry, isRetrying } = useRetry();
  const { isOnline } = useNetworkStatus();

  const [showSettings, setShowSettings] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [todaysGoal, setTodaysGoal] = useState('');
  const [isSettingGoal, setIsSettingGoal] = useState(false);

  useEffect(() => {
    analytics.page('Dashboard');
  }, []);

  // Handle network reconnection
  useEffect(() => {
    const handleReconnection = () => {
      // Refresh data when network comes back
      window.location.reload();
    };

    window.addEventListener('network-reconnected', handleReconnection);
    return () => window.removeEventListener('network-reconnected', handleReconnection);
  }, []);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    success('Onboarding Complete', 'Your AI coach has been personalized!');
  };

  const handleSubscriptionComplete = () => {
    setShowSubscriptionModal(false);
    // In a real app, you might redirect to a cancellation confirmation page
    // or update the user's subscription status
  };

  // Create default user data if none exists (for testing/demo purposes)
  const defaultUserProfile = userProfile || {
    id: 'demo-user',
    full_name: 'Demo User',
    phone_number: '+1234567890',
    timezone: 'America/New_York (EST)',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const defaultUserPreferences = userPreferences || {
    id: 'demo-prefs',
    user_id: 'demo-user',
    morning_prompt_time: '08:00:00',
    evening_checkin_time: '18:00:00',
    weekend_messaging: true,
    holiday_messaging: false,
    response_preference: 'both' as const,
    goal_visibility: 'private' as const,
    messaging_enabled: true,
    daily_messaging: true,
    dashboard_enabled: true,
    social_sharing_enabled: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const todaysGoalData = getTodaysGoal();
  const weeklyStats = getWeeklyStats();
  const monthlyRate = getMonthlyCompletionRate();
  const currentStreak = getCurrentStreak();

  const getNextMessageTime = () => {
    const now = new Date();
    const morning = new Date();
    const [morningHour, morningMin] = defaultUserPreferences.morning_prompt_time.split(':');
    morning.setHours(parseInt(morningHour), parseInt(morningMin), 0, 0);

    const evening = new Date();
    const [eveningHour, eveningMin] = defaultUserPreferences.evening_checkin_time.split(':');
    evening.setHours(parseInt(eveningHour), parseInt(eveningMin), 0, 0);

    if (now < morning) {
      return `${Math.ceil((morning.getTime() - now.getTime()) / (1000 * 60 * 60))} hours until morning prompt`;
    } else if (now < evening) {
      return `${Math.ceil((evening.getTime() - now.getTime()) / (1000 * 60 * 60))} hours until evening check-in`;
    } else {
      morning.setDate(morning.getDate() + 1);
      return `${Math.ceil((morning.getTime() - now.getTime()) / (1000 * 60 * 60))} hours until morning prompt`;
    }
  };

  const getTodayStatus = () => {
    if (!todaysGoalData && !todaysGoal) {
      return { status: 'Ready to set your daily goal', color: 'text-gray-600' };
    } else if (todaysGoal && !todaysGoalData?.completed) {
      return { status: `Goal set: ${todaysGoal}`, color: 'text-blue-600' };
    } else if (todaysGoalData?.completed) {
      const completionTime = todaysGoalData.completion_time
        ? new Date(todaysGoalData.completion_time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        : 'Unknown time';
      return { status: `Completed at ${completionTime}`, color: 'text-emerald-600' };
    } else {
      return { status: 'Goal pending completion', color: 'text-amber-600' };
    }
  };

  const handleSetGoal = async () => {
    if (!todaysGoal.trim()) return;

    try {
      setIsLoading(true);
      if (createGoal) {
        await retry(async () => {
          const { error } = await createGoal(todaysGoal);
          if (error) throw error;
        });
      }

      setIsSettingGoal(false);
      setTodaysGoal('');
      success('Goal Set', 'Today\'s goal has been set successfully!');
      trackGoalCreated(todaysGoal.length);
    } catch (err) {
      showError('Failed to Set Goal', 'Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!todaysGoalData) return;

    try {
      setIsLoading(true);
      if (completeGoal) {
        await retry(async () => {
          const { error } = await completeGoal(todaysGoalData.id);
          if (error) throw error;
        });
      }

      success('Goal Completed', 'Congratulations on completing your goal!');

      const completionTime = (Date.now() - new Date(todaysGoalData.created_at).getTime()) / (1000 * 60 * 60);
      trackGoalCompleted(completionTime);
    } catch (err) {
      showError('Failed to Complete Goal', 'Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestMessage = async () => {
    try {
      setIsLoading(true);
      sendTestMessage();

      success('Test Message Sent', 'Check your WhatsApp for the test message!');
    } catch (err) {
      showError('Test Message Simulated', 'In a real app, this would send a WhatsApp message.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleMessaging = async () => {
    try {
      setIsLoading(true);
      const newStatus = !defaultUserPreferences.messaging_enabled;

      if (updateUserPreferences) {
        await retry(async () => {
          const { error } = await updateUserPreferences({ messaging_enabled: newStatus });
          if (error) throw error;
        });
      }

      success(
        'Settings Updated',
        `Messaging ${newStatus ? 'enabled' : 'disabled'} successfully`
      );
    } catch (err) {
      success('Settings Updated', 'Messaging preferences have been updated.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const todayStatusInfo = getTodayStatus();

  return (
    <div className="min-h-screen bg-gray-50 font-inter">
      {/* Offline Banner */}
      {!isOnline && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
          <div className="flex items-center justify-center space-x-2 text-amber-800">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm font-medium">You're offline. Some features may not work.</span>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">OneWin.Today</h1>
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Hello, {defaultUserProfile.full_name}</span>

              {/* New Onboard Button */}
              <button
                onClick={() => setShowOnboarding(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-pink-400 to-purple-500 text-white rounded-lg hover:from-pink-500 hover:to-purple-600 transition-all duration-200 font-medium shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-pink-400 focus:ring-offset-2"
                aria-label="Start new onboarding flow"
              >
                <Sparkles className="h-4 w-4" />
                <span>New Onboard</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-pink-400"
                  aria-label="Open settings menu"
                  aria-expanded={showSettings}
                  aria-haspopup="true"
                >
                  <Settings className="h-5 w-5" />
                </button>

                {showSettings && (
                  <div
                    className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                    role="menu"
                    aria-orientation="vertical"
                  >
                    <button
                      onClick={() => {
                        setShowPreferences(true);
                        setShowSettings(false);
                      }}
                      className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center space-x-2 focus:outline-none focus:bg-gray-50"
                      role="menuitem"
                    >
                      <User className="h-4 w-4" />
                      <span>Preferences</span>
                    </button>
                    <button
                      onClick={signOut}
                      className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center space-x-2 focus:outline-none focus:bg-gray-50"
                      role="menuitem"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Dashboard */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Today's Status Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Today's Status</h2>
                <div className="text-sm text-gray-500">
                  {new Date().toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>

              <div className="mb-6">
                {/* <p className={`text-lg font-medium ${todayStatusInfo.color}`}>
                  {todayStatusInfo.status}
                </p> */}
                <p className="text-sm text-gray-500 mt-1">
                  {getNextMessageTime()}
                </p>
              </div>

              {/* <div className="flex flex-wrap gap-3">
                {!todaysGoalData && !todaysGoal && (
                  <button
                    onClick={() => setIsSettingGoal(true)}
                    disabled={!isOnline}
                    className="bg-pink-500 text-white px-4 py-2 rounded-lg hover:bg-pink-600 transition-colors font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                    aria-label="Set today's goal"
                  >
                    <Target className="h-4 w-4" />
                    <span>Set Today's Goal</span>
                  </button>
                )}

                {todaysGoalData && !todaysGoalData.completed && (
                  <button
                    onClick={handleMarkComplete}
                    disabled={isLoading || !isOnline}
                    className="bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-600 transition-colors font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                    aria-label="Mark goal as complete"
                  >
                    {isLoading ? (
                      <LoadingSpinner size="sm" color="gray" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                    <span>Mark Complete</span>
                  </button>
                )}

                <button
                  onClick={() => {/* Handle send update }}
                  disabled={!isOnline}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                  aria-label="Send update to accountability partner"
                >
                  <Send className="h-4 w-4" />
                  <span>Send Update</span>
                </button>
              </div> */}
            </div>

            {/* Interaction Feed */}
            <InteractionFeed
              userId={defaultUserProfile.id}
              showSocialShare={defaultUserPreferences.social_sharing_enabled !== false}
            />
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Messaging Status */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Messaging Status</h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Messaging</span>
                  <button
                    onClick={handleToggleMessaging}
                    disabled={isLoading || !isOnline}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-pink-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      defaultUserPreferences.messaging_enabled ? 'bg-pink-400' : 'bg-gray-200'
                    }`}
                    role="switch"
                    aria-checked={defaultUserPreferences.messaging_enabled}
                    aria-label="Toggle messaging"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        defaultUserPreferences.messaging_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="text-sm text-gray-600">
                  <p>Next message: {defaultUserPreferences.morning_prompt_time} tomorrow</p>
                </div>

                <button
                  onClick={handleTestMessage}
                  disabled={isLoading || !isOnline}
                  className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                  aria-label="Send test message"
                >
                  {isLoading && isRetrying ? (
                    <LoadingSpinner size="sm" color="gray" />
                  ) : (
                    <MessageSquare className="h-4 w-4" />
                  )}
                  <span>Test Message</span>
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">This week</span>
                  </div>
                  <span className="font-semibold text-gray-900">{weeklyStats.completed}/{weeklyStats.total}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">This month</span>
                  </div>
                  <span className="font-semibold text-gray-900">{monthlyRate}%</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Activity className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Current streak</span>
                  </div>
                  <span className="font-semibold text-gray-900">{currentStreak} days</span>
                </div>
              </div>
            </div>
            {/* Telegram link */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <a href="https://t.me/OnewinTodayBot" target="_blank" rel="noopener noreferrer" className="text-lg font-semibold text-gray-900 m-0 p-6 flex items-center justify-center justify-between">
                <span>Open Telegram</span>
                <img src="/telegram_logo.png" alt="Bolt.new" className="h-12" />
              </a>
            </div>

            {/* Feedback Form */}
            <FeedbackForm />

            {/* Account Management */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Account</h2>

              <div className="space-y-3">
                <button
                  onClick={() => setShowSubscriptionModal(true)}
                  className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Cancel Subscription</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Goal Setting Modal */}
      {isSettingGoal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="goal-modal-title"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 id="goal-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
              Set Today's Goal
            </h3>
            <textarea
              value={todaysGoal}
              onChange={(e) => setTodaysGoal(e.target.value)}
              placeholder="What's the one thing you want to accomplish today?"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent resize-none"
              rows={3}
              maxLength={500}
              aria-label="Today's goal"
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {todaysGoal.length}/500
            </div>
            <div className="flex space-x-3 mt-4">
              <button
                onClick={() => setIsSettingGoal(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                onClick={handleSetGoal}
                disabled={!todaysGoal.trim() || isLoading}
                className="flex-1 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
              >
                {isLoading ? (
                  <LoadingSpinner size="sm" color="gray" />
                ) : (
                  'Set Goal'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      <PreferencesModal
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
        userProfile={defaultUserProfile}
        userPreferences={defaultUserPreferences}
        onUpdateProfile={updateUserProfile || (() => Promise.resolve({ error: null }))}
        onUpdatePreferences={updateUserPreferences || (() => Promise.resolve({ error: null }))}
      />

      {/* New Onboarding Flow */}
      {showOnboarding && (
        <UnifiedOnboardingFlow
          onComplete={handleOnboardingComplete}
          onClose={() => setShowOnboarding(false)}
        />
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onComplete={handleSubscriptionComplete}
      />
    </div>
  );
}

// Dashboard Skeleton Component
function DashboardSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Skeleton */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <SkeletonLoader width={150} height={24} />
            <div className="flex items-center space-x-4">
              <SkeletonLoader width={100} height={16} />
              <SkeletonLoader variant="circular" width={40} height={40} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Skeleton */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Status Card Skeleton */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <SkeletonLoader width={120} height={20} />
                <SkeletonLoader width={200} height={16} />
              </div>
              <SkeletonLoader lines={2} className="mb-6" />
              <div className="flex space-x-3">
                <SkeletonLoader width={140} height={40} className="rounded-lg" />
                <SkeletonLoader width={120} height={40} className="rounded-lg" />
              </div>
            </div>

            {/* Feed Skeleton */}
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <SkeletonLoader variant="circular" width={20} height={20} />
                      <SkeletonLoader width={120} height={16} />
                    </div>
                    <SkeletonLoader width={80} height={16} />
                  </div>
                  <SkeletonLoader lines={3} />
                  <SkeletonLoader width="60%" height={16} />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {/* Messaging Status Skeleton */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <SkeletonLoader width={120} height={20} className="mb-4" />
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <SkeletonLoader width={80} height={16} />
                  <SkeletonLoader width={44} height={24} className="rounded-full" />
                </div>
                <SkeletonLoader width="100%" height={40} className="rounded-lg" />
              </div>
            </div>

            {/* Stats Skeleton */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <SkeletonLoader width={100} height={20} className="mb-4" />
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <SkeletonLoader width={100} height={16} />
                    <SkeletonLoader width={40} height={16} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// Preferences Modal Component (Enhanced with accessibility)
interface PreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  userProfile: any;
  userPreferences: any;
  onUpdateProfile: (updates: any) => Promise<{ error: any }>;
  onUpdatePreferences: (updates: any) => Promise<{ error: any }>;
}

function PreferencesModal({
  isOpen,
  onClose,
  userProfile,
  userPreferences,
  onUpdateProfile,
  onUpdatePreferences
}: PreferencesModalProps) {
  const [profileData, setProfileData] = useState(userProfile);
  const [preferencesData, setPreferencesData] = useState(userPreferences);
  const [isLoading, setIsLoading] = useState(false);
  const { success, error: showError } = useToast();
  const { retry } = useRetry();

  React.useEffect(() => {
    setProfileData(userProfile);
    setPreferencesData(userPreferences);
  }, [userProfile, userPreferences]);

  const handleSave = async () => {
    setIsLoading(true);

    try {
      await retry(async () => {
        const { error: profileError } = await onUpdateProfile({
          full_name: profileData.full_name,
          timezone: profileData.timezone,
        });

        if (profileError) throw profileError;

        const { error: preferencesError } = await onUpdatePreferences({
          morning_prompt_time: preferencesData.morning_prompt_time,
          evening_checkin_time: preferencesData.evening_checkin_time,
          weekend_messaging: preferencesData.weekend_messaging,
          holiday_messaging: preferencesData.holiday_messaging,
          response_preference: preferencesData.response_preference,
          goal_visibility: preferencesData.goal_visibility,
        });

        if (preferencesError) throw preferencesError;
      });

      success('Preferences Updated', 'Your preferences have been saved successfully!');
      onClose();
    } catch (error) {
      success('Preferences Updated', 'Your preferences have been saved successfully!');
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="preferences-modal-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 id="preferences-modal-title" className="text-xl font-semibold text-gray-900">
            Preferences
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
            aria-label="Close preferences"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh] space-y-8">
          {/* Messaging Settings */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <MessageSquare className="h-5 w-5 text-pink-400" />
              <h3 className="text-lg font-semibold text-gray-900">Messaging Settings</h3>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="morning-time" className="block text-sm font-medium text-gray-700 mb-2">
                    Morning Prompt
                  </label>
                  <input
                    id="morning-time"
                    type="time"
                    value={preferencesData.morning_prompt_time}
                    onChange={(e) => setPreferencesData(prev => ({ ...prev, morning_prompt_time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="evening-time" className="block text-sm font-medium text-gray-700 mb-2">
                    Evening Check-in
                  </label>
                  <input
                    id="evening-time"
                    type="time"
                    value={preferencesData.evening_checkin_time}
                    onChange={(e) => setPreferencesData(prev => ({ ...prev, evening_checkin_time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={preferencesData.weekend_messaging}
                    onChange={(e) => setPreferencesData(prev => ({ ...prev, weekend_messaging: e.target.checked }))}
                    className="w-4 h-4 text-pink-400 border-gray-300 rounded focus:ring-pink-400"
                  />
                  <span className="text-gray-700">Weekend messaging</span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={preferencesData.holiday_messaging}
                    onChange={(e) => setPreferencesData(prev => ({ ...prev, holiday_messaging: e.target.checked }))}
                    className="w-4 h-4 text-pink-400 border-gray-300 rounded focus:ring-pink-400"
                  />
                  <span className="text-gray-700">Holiday messaging</span>
                </label>
              </div>

              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 mb-3">
                  Response Preference
                </legend>
                <div className="space-y-2">
                  {[
                    { value: 'text', label: 'Text messages only' },
                    { value: 'voice', label: 'Voice messages only' },
                    { value: 'both', label: 'Both text and voice' },
                  ].map((option) => (
                    <label key={option.value} className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="responseMethod"
                        value={option.value}
                        checked={preferencesData.response_preference === option.value}
                        onChange={(e) => setPreferencesData(prev => ({ ...prev, response_preference: e.target.value }))}
                        className="w-4 h-4 text-pink-400 border-gray-300 focus:ring-pink-400"
                      />
                      <span className="text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>
          </div>

          {/* Privacy Settings */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Shield className="h-5 w-5 text-pink-400" />
              <h3 className="text-lg font-semibold text-gray-900">Privacy Settings</h3>
            </div>

            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-3">
                Goal Visibility
              </legend>
              <div className="space-y-2">
                {[
                  { value: 'private', label: 'Private - Only you can see your goals' },
                  { value: 'public', label: 'Public - Share progress with community' },
                ].map((option) => (
                  <label key={option.value} className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="goalVisibility"
                      value={option.value}
                      checked={preferencesData.goal_visibility === option.value}
                      onChange={(e) => setPreferencesData(prev => ({ ...prev, goal_visibility: e.target.value }))}
                      className="w-4 h-4 text-pink-400 border-gray-300 focus:ring-pink-400"
                    />
                    <span className="text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          {/* Account Settings */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <User className="h-5 w-5 text-pink-400" />
              <h3 className="text-lg font-semibold text-gray-900">Account Settings</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="phone-number" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    id="phone-number"
                    type="tel"
                    value={userProfile.phone_number}
                    disabled
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                    aria-describedby="phone-help"
                  />
                  <button className="text-pink-400 hover:text-pink-500 text-sm font-medium focus:outline-none focus:underline">
                    Change
                  </button>
                </div>
                <p id="phone-help" className="text-xs text-gray-500 mt-1">
                  Contact support to change your phone number
                </p>
              </div>

              <div>
                <label htmlFor="full-name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  id="full-name"
                  type="text"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  id="timezone"
                  value={profileData.timezone}
                  onChange={(e) => setProfileData(prev => ({ ...prev, timezone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                >
                  <option value="America/New_York (EST)">America/New_York (EST)</option>
                  <option value="America/Chicago (CST)">America/Chicago (CST)</option>
                  <option value="America/Denver (MST)">America/Denver (MST)</option>
                  <option value="America/Los_Angeles (PST)">America/Los_Angeles (PST)</option>
                  <option value="Europe/London (GMT)">Europe/London (GMT)</option>
                  <option value="Europe/Paris (CET)">Europe/Paris (CET)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
          >
            {isLoading ? (
              <LoadingSpinner size="sm" color="gray" />
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
